import { extractChannelId } from "@/lib/ws/routing";
import type { ChannelMessageHandler, RealtimeConnectionState } from "@/lib/ws/types";

import { MOCK_REALTIME_URL } from "../constants";
import { MOCK_DEVICE_IDS } from "../data/devices";
import { randomMockAlertPayload } from "../generators/alerts";
import { generateLiveTelemetryPoint } from "../generators/telemetry";

/**
 * Fake transport compatible with {@link wireRealtimeClient} expectations:
 * `connect` / `disconnect` / `subscribe` / `onConnectionState`.
 */
export class MockRealtimeClient {
  private state: RealtimeConnectionState = "disconnected";
  private intentionalClose = false;
  private telemetryTimer: ReturnType<typeof setInterval> | null = null;
  private alertTimer: ReturnType<typeof setInterval> | null = null;

  private readonly subscribers = new Map<string, Set<ChannelMessageHandler>>();
  private readonly connectionListeners = new Set<(s: RealtimeConnectionState) => void>();

  getConnectionState(): RealtimeConnectionState {
    return this.state;
  }

  onConnectionState(listener: (state: RealtimeConnectionState) => void): () => void {
    this.connectionListeners.add(listener);
    listener(this.state);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  connect(): void {
    this.intentionalClose = false;
    if (this.telemetryTimer !== null) {
      return;
    }
    this.setState("connecting");
    queueMicrotask(() => {
      if (this.intentionalClose) {
        return;
      }
      this.setState("connected");
      this.telemetryTimer = setInterval(() => this.emitTelemetryBurst(), 1000);
      this.alertTimer = setInterval(() => {
        if (Math.random() < 0.12) {
          this.dispatch(randomMockAlertPayload());
        }
      }, 1000);
    });
  }

  disconnect(): void {
    this.intentionalClose = true;
    if (this.telemetryTimer !== null) {
      clearInterval(this.telemetryTimer);
      this.telemetryTimer = null;
    }
    if (this.alertTimer !== null) {
      clearInterval(this.alertTimer);
      this.alertTimer = null;
    }
    this.setState("disconnected");
  }

  subscribe(channel: string, handler: ChannelMessageHandler): () => void {
    let set = this.subscribers.get(channel);
    if (!set) {
      set = new Set();
      this.subscribers.set(channel, set);
    }
    set.add(handler);
    return () => this.unsubscribe(channel, handler);
  }

  unsubscribe(channel: string, handler?: ChannelMessageHandler): void {
    if (handler === undefined) {
      this.subscribers.delete(channel);
      return;
    }
    const set = this.subscribers.get(channel);
    if (!set) {
      return;
    }
    set.delete(handler);
    if (set.size === 0) {
      this.subscribers.delete(channel);
    }
  }

  private setState(next: RealtimeConnectionState): void {
    if (this.state === next) {
      return;
    }
    this.state = next;
    this.connectionListeners.forEach((fn) => {
      try {
        fn(next);
      } catch {
        /* isolate */
      }
    });
  }

  private dispatch(parsed: unknown): void {
    const channelId = extractChannelId(parsed);
    if (channelId) {
      const set = this.subscribers.get(channelId);
      if (set) {
        set.forEach((fn) => {
          try {
            fn(parsed, new MessageEvent("message", { data: JSON.stringify(parsed) }));
          } catch {
            /* isolate */
          }
        });
      }
    }
    const star = this.subscribers.get("*");
    if (star) {
      star.forEach((fn) => {
        try {
          fn(parsed, new MessageEvent("message", { data: JSON.stringify(parsed) }));
        } catch {
          /* isolate */
        }
      });
    }
  }

  private emitTelemetryBurst(): void {
    const metrics = ["temperature", "humidity", "pressure"];
    const slice = MOCK_DEVICE_IDS.slice(0, 4);
    for (const deviceId of slice) {
      const data = generateLiveTelemetryPoint(deviceId, metrics);
      const msg = {
        channel: "telemetry",
        device_id: deviceId,
        time: new Date().toISOString(),
        data,
      };
      this.dispatch(msg);
    }
  }
}

export function isMockRealtimeUrl(url: string): boolean {
  return url === MOCK_REALTIME_URL || url.startsWith("mock:");
}

export function createMockRealtimeClient(): MockRealtimeClient {
  return new MockRealtimeClient();
}
